/**
 * Edit check_schema (Phase 6) - add/rename/reorder checks, toggle active, scope to a
 * bank. This table is the single source of truth for both the compliance prompt and the
 * dashboard grid (see worker/src/admin/checkSchema.ts), so every save here changes both;
 * the preview below re-renders the same ChecklistSection component the reviewer sees.
 */
import { useEffect, useState } from 'react';
import { fetchAllCheckSchema, fetchAllBanks } from '../../admin/queries';
import { createCheckSchemaRow, updateCheckSchemaRow, reorderCheckSchema, ApiError } from '../../api';
import type { AdminCheckSchemaRow, AdminBankRow } from '../../admin/types';
import { Banner, Button, Chip, Spinner, Tile } from '../../components/ui';
import { ChecklistSection } from '../../components/reviewer/ChecklistSection';

interface DraftRow extends AdminCheckSchemaRow {
  dirty: boolean;
}

const emptyNewCheck = {
  checkKey: '',
  category: '',
  label: '',
  description: '',
  bankId: '' as string,
};

export function CheckSchemaAdmin() {
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [banks, setBanks] = useState<AdminBankRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [newCheck, setNewCheck] = useState(emptyNewCheck);

  async function load() {
    setError(null);
    try {
      const [checks, bankRows] = await Promise.all([fetchAllCheckSchema(), fetchAllBanks()]);
      setRows(checks.map((c) => ({ ...c, dirty: false })));
      setBanks(bankRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the check schema.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!rows) return <Spinner label="Loading checks…" />;

  const categories: { category: string; items: DraftRow[] }[] = [];
  for (const row of rows) {
    let group = categories.find((g) => g.category === row.category);
    if (!group) {
      group = { category: row.category, items: [] };
      categories.push(group);
    }
    group.items.push(row);
  }

  function patchRow(checkKey: string, patch: Partial<DraftRow>) {
    setRows((prev) => (prev ? prev.map((r) => (r.checkKey === checkKey ? { ...r, ...patch, dirty: true } : r)) : prev));
  }

  async function save(row: DraftRow) {
    setBusyKey(row.checkKey);
    setError(null);
    try {
      await updateCheckSchemaRow(row.checkKey, {
        category: row.category,
        label: row.label,
        description: row.description,
        displayOrder: row.displayOrder,
        handlerType: row.handlerType,
        bankId: row.bankId,
        active: row.active,
      });
      setRows((prev) => (prev ? prev.map((r) => (r.checkKey === row.checkKey ? { ...r, dirty: false } : r)) : prev));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that check.');
    } finally {
      setBusyKey(null);
    }
  }

  async function move(group: DraftRow[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= group.length) return;
    const a = group[index]!;
    const b = group[target]!;
    setBusyKey(a.checkKey);
    setError(null);
    try {
      await reorderCheckSchema([
        { checkKey: a.checkKey, displayOrder: b.displayOrder },
        { checkKey: b.checkKey, displayOrder: a.displayOrder },
      ]);
      setRows((prev) =>
        prev
          ? prev.map((r) => {
              if (r.checkKey === a.checkKey) return { ...r, displayOrder: b.displayOrder };
              if (r.checkKey === b.checkKey) return { ...r, displayOrder: a.displayOrder };
              return r;
            })
          : prev,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reorder those checks.');
    } finally {
      setBusyKey(null);
    }
  }

  async function createCheck() {
    if (!newCheck.checkKey.trim() || !newCheck.category.trim() || !newCheck.label.trim()) {
      setError('check_key, category, and label are required to add a check.');
      return;
    }
    setBusyKey('__new__');
    setError(null);
    try {
      await createCheckSchemaRow({
        checkKey: newCheck.checkKey.trim(),
        category: newCheck.category.trim(),
        label: newCheck.label.trim(),
        description: newCheck.description.trim() || null,
        displayOrder: rows && rows.length > 0 ? Math.max(...rows.map((r) => r.displayOrder)) + 10 : 10,
        handlerType: 'static',
        bankId: newCheck.bankId || null,
        active: true,
      });
      setNewCheck(emptyNewCheck);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that check.');
    } finally {
      setBusyKey(null);
    }
  }

  const activeSchemaForPreview = rows
    .filter((r) => r.active)
    .map((r) => ({ checkKey: r.checkKey, category: r.category, label: r.label, description: r.description, displayOrder: r.displayOrder }));

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}

      {categories.map((group) => (
        <Tile key={group.category} className="admin-card">
          <h3 className="checklist-cat__title">{group.category}</h3>
          <div className="admin-rows">
            {group.items.map((row, index) => (
              <div className="admin-row" key={row.checkKey}>
                <div className="admin-row__order">
                  <Button variant="ghost" onClick={() => void move(group.items, index, -1)} disabled={index === 0 || busyKey === row.checkKey}>
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => void move(group.items, index, 1)}
                    disabled={index === group.items.length - 1 || busyKey === row.checkKey}
                  >
                    ↓
                  </Button>
                </div>
                <div className="admin-row__fields">
                  <input
                    className="input"
                    value={row.label}
                    onChange={(e) => patchRow(row.checkKey, { label: e.target.value })}
                    placeholder="Label"
                  />
                  <input
                    className="input"
                    value={row.category}
                    onChange={(e) => patchRow(row.checkKey, { category: e.target.value })}
                    placeholder="Category"
                  />
                  <textarea
                    className="input"
                    value={row.description ?? ''}
                    onChange={(e) => patchRow(row.checkKey, { description: e.target.value })}
                    placeholder="Description"
                    rows={2}
                  />
                  <select
                    className="input"
                    value={row.bankId ?? ''}
                    onChange={(e) => patchRow(row.checkKey, { bankId: e.target.value || null })}
                  >
                    <option value="">All banks</option>
                    {banks.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <label className="admin-row__toggle">
                    <input type="checkbox" checked={row.active} onChange={(e) => patchRow(row.checkKey, { active: e.target.checked })} />
                    Active
                  </label>
                  <Chip>{row.checkKey}</Chip>
                </div>
                <Button onClick={() => void save(row)} disabled={!row.dirty || busyKey === row.checkKey}>
                  {busyKey === row.checkKey ? 'Saving…' : 'Save'}
                </Button>
              </div>
            ))}
          </div>
        </Tile>
      ))}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Add a check</h3>
        <div className="admin-new-form">
          <input
            className="input"
            placeholder="check_key (e.g. property.solar_panels)"
            value={newCheck.checkKey}
            onChange={(e) => setNewCheck((n) => ({ ...n, checkKey: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Category"
            value={newCheck.category}
            onChange={(e) => setNewCheck((n) => ({ ...n, category: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Label"
            value={newCheck.label}
            onChange={(e) => setNewCheck((n) => ({ ...n, label: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Description (optional)"
            value={newCheck.description}
            onChange={(e) => setNewCheck((n) => ({ ...n, description: e.target.value }))}
          />
          <select className="input" value={newCheck.bankId} onChange={(e) => setNewCheck((n) => ({ ...n, bankId: e.target.value }))}>
            <option value="">All banks</option>
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <Button onClick={() => void createCheck()} disabled={busyKey === '__new__'}>
            {busyKey === '__new__' ? 'Adding…' : 'Add check'}
          </Button>
        </div>
      </Tile>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Grid preview</h2>
        </div>
        <ChecklistSection
          schema={activeSchemaForPreview}
          results={[]}
          standingInstructionLabel="Preview"
          checked={false}
          onJump={() => {}}
        />
      </section>
    </div>
  );
}
